import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useRecoilValue } from "recoil"
import { userAtom } from "@/atoms/userAtom"
import { Button, ColorPicker, Form, Input, Modal, Typography } from "antd"
import KosmoButton from "@/UI/Buttons/KosmoButton"
import { useOrg } from "@/hooks/useOrg"
import UploadProofOfDeliveries from "@/modules/active-orders/shared/UploadProofOfDeliveries"
import { useUser } from "@/hooks/useUser"
import { last } from "lodash"

const { Title } = Typography

interface BrandingFormProps {}

const BrandingForm: React.FC<BrandingFormProps> = () => {
  const { t } = useTranslation()
  const { updateOrgPreferences, isLoading } = useOrg()
  const { preference } = useRecoilValue(userAtom)
  const { fetchUserProfile } = useUser()
  const [brandingForm] = Form.useForm()
  const { branding } = preference
  const { height = 42, width = 42 } = branding?.width ? branding : { height: 42, width: 42 }
  const [isModalPreviewOpen, setIsModalPreviewOpen] = useState(false)
  const [isMobilePreview, setIsMobilePreview] = useState(false)
  const [logoHash, setLogoHash] = useState<number>(Math.random())
  useEffect(() => {
    brandingForm.setFieldsValue({
      companyName: branding?.companyName,
      companyLogoUrl: branding?.companyLogoUrl,
      companyWebsiteUrl: branding?.companyWebsiteUrl,
      primaryColor: branding?.primaryColor?.toUpperCase(),
      secondaryColor: branding?.secondaryColor?.toUpperCase(),
      logoAspectRatio: (height / width)?.toFixed(1),
      platformEnabled: branding?.platformEnabled,
    })
  }, [branding])
  const currentLogo = Form.useWatch("companyLogoUrl", brandingForm)
  const currentWebsiteUrl = Form.useWatch("companyWebsiteUrl", brandingForm)
  const currentPrimaryColor = Form.useWatch("primaryColor", brandingForm)
  const currentSecondaryColor = Form.useWatch("secondaryColor", brandingForm)
  const currentCompanyName = Form.useWatch("companyName", brandingForm)
  const currentAspectRatio = Form.useWatch("logoAspectRatio", brandingForm)

  const correctedWidth = parseFloat(currentAspectRatio) > 1 ? 42 : 42 / currentAspectRatio
  const correctedHeight = parseFloat(currentAspectRatio) > 1 ? 42 * currentAspectRatio : 42

  const onFinishBranding = async (formValues: any) => {
    try {
      const logoUrlKey = last(currentLogo?.split("share.kosmo.delivery")) as string
      await updateOrgPreferences({
        ...preference,
        branding: {
          companyLogoUrl: logoUrlKey?.replace("/branding", "branding").split("?")?.[0],
          companyWebsiteUrl: formValues?.companyWebsiteUrl,
          primaryColor: formValues?.primaryColor,
          secondaryColor: formValues?.secondaryColor,
          companyName: formValues?.companyName,
          width: correctedWidth,
          height: correctedHeight,
          platformEnabled: branding?.platformEnabled,
        },
      })
      await fetchUserProfile()
    } catch (error) {}
  }

  const isBrandingModified =
    currentLogo !== branding?.companyLogoUrl ||
    currentPrimaryColor !== branding?.primaryColor ||
    currentSecondaryColor !== branding?.secondaryColor ||
    currentCompanyName !== branding?.companyName ||
    currentWebsiteUrl !== branding?.companyWebsiteUrl ||
    currentAspectRatio != (height / width)?.toFixed(1)

  const isPreviewAvailable = !!currentLogo && !!currentCompanyName

  const trackingPreviewLink = isPreviewAvailable
    ? `${
        process.env.NEXT_PUBLIC_TRACKING_HOST
      }/demo?companyName=${currentCompanyName}&companyLogoUrl=${currentLogo}&primaryColor=${
        currentPrimaryColor ? currentPrimaryColor?.replace("#", "") : ""
      }&secondaryColor=${
        currentSecondaryColor ? currentSecondaryColor?.replace("#", "") : ""
      }&companyWebsiteUrl=${currentWebsiteUrl}&logoAspectRatio=${currentAspectRatio}`
    : ""

  return (
    <>
      <Modal
        key={logoHash}
        title={
          <Button
            type="primary"
            className="cursor-pointer"
            onClick={() => setIsMobilePreview(!isMobilePreview)}
          >
            {isMobilePreview ? t("common.seeOnDesktop") : t("common.seeOnMobile")}
          </Button>
        }
        open={isModalPreviewOpen}
        centered
        onCancel={() => setIsModalPreviewOpen(false)}
        footer={null}
        width={"auto"}
        styles={{
          content: {
            width: isMobilePreview ? 480 : 1200,
            height: isMobilePreview ? 780 : 900,
          },
        }}
      >
        {isPreviewAvailable && (
          <iframe
            key={logoHash}
            style={{ width: isMobilePreview ? 435 : 1150, height: isMobilePreview ? 700 : 825 }}
            src={trackingPreviewLink}
          />
        )}
      </Modal>
      <Title level={5}>{t("common.brandCustomization")}</Title>
      <Form
        id="brand-settings-form"
        form={brandingForm}
        colon={false}
        className="gap-y-4"
        validateTrigger="onSubmit"
        labelCol={{ span: 6 }}
        labelAlign="left"
        wrapperCol={{ span: 14 }}
        requiredMark={false}
        onFinish={onFinishBranding}
      >
        <Form.Item
          name="companyName"
          required
          // rules={[{ required: true, message: t("common.forms.required") }]}
          label={<span className="font-semibold">{t("common.companyName")}</span>}
        >
          <Input placeholder={t("common.inputBrandName")} />
        </Form.Item>
        <Form.Item
          name="companyWebsiteUrl"
          label={<span className="font-semibold">{t("common.companyWebsiteUrl")}</span>}
        >
          <Input placeholder={t("common.yourWebsiteUrl")} type="text" />
        </Form.Item>
        <Form.Item
          name="companyLogoUrl"
          required
          className="mb-4"
          // rules={[{ required: true, message: t("common.forms.required") }]}
          label={<span className="font-semibold">{t("common.companyLogo")}</span>}
        >
          <div className="flex flex-row gap-x-2">
            <UploadProofOfDeliveries
              uploadURL={branding?.uploadLogoForm?.uploadUrl}
              data={branding?.uploadLogoForm?.formData}
              skipFilename
              uploadPodCallback={async () => {
                brandingForm.setFieldValue(
                  "companyLogoUrl",
                  branding?.uploadLogoForm?.formData["key"]
                )
                brandingForm.setFieldValue("logoAspectRatio", 1)
                setLogoHash(Math.random())
                brandingForm.submit()
              }}
              filesLimit={10}
              accept="image/png"
            />
            <div
              className="flex w-[250px] items-center justify-center rounded-md"
              style={{
                backgroundColor: currentSecondaryColor,
              }}
            >
              {preference?.branding?.companyLogoUrl && logoHash && (
                <img
                  src={preference?.branding?.companyLogoUrl}
                  style={{
                    width: correctedWidth,
                    height: correctedHeight,
                  }}
                />
              )}
            </div>
          </div>
        </Form.Item>
        <Form.Item
          name="logoAspectRatio"
          label={<span className="font-semibold">{t("common.logoAspectRatio")}</span>}
          className="items-center"
          wrapperCol={{ span: 2 }}
        >
          <Input type="number" min={0} max={3} step={0.05} placeholder="1" />
        </Form.Item>
        <Form.Item
          name="primaryColor"
          label={<span className="font-semibold">{t("common.primaryColor")}</span>}
          className="items-center"
          trigger="never"
        >
          <ColorPicker
            key="primaryColor"
            placement="top"
            disabledAlpha
            defaultValue="#4100FF"
            showText={currentPrimaryColor}
            size="large"
            style={{ border: "none" }}
            format="hex"
            defaultFormat="hex"
            onChange={(_, hex: string) => {
              brandingForm.setFieldValue("primaryColor", hex?.toUpperCase())
            }}
          />
        </Form.Item>
        <Form.Item
          name="secondaryColor"
          label={<span className="font-semibold">{t("common.secondaryColor")}</span>}
          className="items-center"
          trigger="never"
        >
          <ColorPicker
            key="secondaryColor"
            placement="top"
            disabledAlpha
            value={currentSecondaryColor}
            showText={currentSecondaryColor}
            size="large"
            style={{ border: "none" }}
            format="hex"
            defaultFormat="hex"
            onChange={(_, hex: string) => {
              brandingForm.setFieldValue("secondaryColor", hex?.toUpperCase())
            }}
          />
        </Form.Item>
        {/* {!branding?.platformEnabled && (
          <Form.Item
            tooltip={{
              icon: (
                <FontAwesomeIcon
                  className={`cursor-pointer`}
                  icon={faQuestionCircle}
                  style={{
                    color:
                      branding?.platformEnabled && branding?.primaryColor
                        ? branding?.primaryColor
                        : "#4100FF",
                  }}
                />
              ),
              title: t("common.enableForPlatformTooltip"),
            }}
            name="platformEnabled"
            label={
              <div className="flex gap-x-2 font-semibold items-center">
                <p>{t("common.enableForPlatform")}</p>
              </div>
            }
            className="items-center"
            wrapperCol={{ span: 2 }}
          >
            <Switch checked={branding?.platformEnabled} disabled={true} />
          </Form.Item>
        )} */}
        <div className="flex gap-x-4">
          <KosmoButton
            htmlType="submit"
            isDisabled={!isBrandingModified || isLoading}
            isLoading={isLoading}
          >
            {t("common.save")}
          </KosmoButton>
          <KosmoButton
            isDisabled={!isPreviewAvailable || isLoading}
            type="default"
            onClick={() => {
              setIsModalPreviewOpen(true)
            }}
          >
            {t("common.preview")}
          </KosmoButton>
          {(branding?.companyLogoUrl || branding?.companyName || branding?.primaryColor) && (
            <KosmoButton
              isDisabled={!branding}
              type="default"
              onClick={async () => {
                await updateOrgPreferences({
                  ...preference,
                  branding: {},
                })
                brandingForm?.resetFields()
              }}
            >
              {t("common.reset")}
            </KosmoButton>
          )}
        </div>
      </Form>
    </>
  )
}

export default BrandingForm
